import { State } from '@meraki-internal/state';
import { ICareMap } from '../../caremap/ICareMap';
import { CareMapState } from '../../caremap/CareMapState';
import { WithLinks } from '../../caremap/WithLinks';
import { ICaremapNote, ICaremapNoteResponse, INoteReactions, NotesClient } from './NotesClient';
import { ICareMapNoteTarget } from './ICareMapNoteTarget';
import { AlertPresenter } from '../../AlertBinder';
import { ReactionsModel, NoteReactionsModelFactory } from '../../community/reactions/ReactionsModel';
import { nanoid } from 'nanoid';
import { CareMapNoteTracker } from './CareMapNoteTracker';
import { ToastPresenter } from '../../ToastBinder';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { NewNoteFormViewModel } from '../add-note/NewNoteFormViewModel';
import { EditNoteFormViewModel } from '../edit-note/EditNoteFormViewModel';
import { FileUploader } from '../generic-notes/attachments/FileUploader';
import { NoteFormTotalAttachmentCountHelper } from './NoteFormTotalAttachmentCountHelper';
import { NoteValidator } from './NoteValidator';
import { BufferedEventBus } from '../../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../../web-sockets/SocketState';
import { Logger } from '../../support/Logger';

export class CaremapNotesModelFactory extends State<Record<string, never>> {
    static inject = () => [
        CareMapState,
        NotesClient,
        AlertPresenter,
        NoteReactionsModelFactory,
        CareMapNoteTracker,
        ToastPresenter,
        HistoryViewModel,
        FileUploader,
        NoteFormTotalAttachmentCountHelper,
        NoteValidator,
        BufferedEventBus,
        Logger
    ];
    constructor(
        public careMapState: CareMapState,
        public notesClient: NotesClient,
        public alert: AlertPresenter,
        public reactionsFactory: NoteReactionsModelFactory,
        public tracking: CareMapNoteTracker,
        public toastPresenter: ToastPresenter,
        public history: HistoryViewModel,
        public fileUploader: FileUploader,
        public attachmentCountHelper: NoteFormTotalAttachmentCountHelper,
        public validator: NoteValidator,
        public events: BufferedEventBus,
        public logger: Logger
    ) {
        super({});
        this.attachmentCountHelper.factory = this;
    }

    private careMapIdToModel: { [careMapId: string]: NotesModel } = {};

    forActiveCaremap = (): NotesModel => {
        const activeCaremap = this.careMapState.getActiveCaremap() as WithLinks<ICareMap>;
        if (!activeCaremap) {
            // TODO: consider instead returning the most recent one
            // to avoid transition errors
            throw new Error('there is no active caremap');
        }
        if (!this.careMapIdToModel[activeCaremap.careMapId!]) {
            const model = new NotesModel(activeCaremap, this);


            model.load().catch(err => {
                this.alert.showAndLogError(err);
            });

            // forActiveCaremap is likely called in a render
            // so we need to avoid doing anything sync that might change state
            // or react gets upset
            setTimeout(() => {
                // if the model changes, we change, that way components can just subscrive to the factory
                model.subscribe(() => {
                    this.setState({});
                });
            });


            this.careMapIdToModel[activeCaremap.careMapId!] = model;
        }
        return this.careMapIdToModel[activeCaremap.careMapId!];
    };

    createFormForNewNote = (noteTarget: ICareMapNoteTarget = { section: 'patient' }, note: Partial<ICaremapNote> = {}): NewNoteFormViewModel => {
        const newNote: ICaremapNote = {
            noteId: nanoid(),
            title: '',
            html: '',
            isPrivate: true,
            attachments: [],
            ...note,
            otherAttributes: {
                noteTarget
            }
        };
        return new NewNoteFormViewModel(newNote, this);
    };

    createEditForm = async (noteId: string): Promise<EditNoteFormViewModel> => {

        while (!this.forActiveCaremap().hasLoaded) {
            // TODO: timeout
            await new Promise(resolve => setTimeout(resolve, 250));
        }

        const note = this.forActiveCaremap().getNote(noteId);
        if (!note) {
            throw new Error('note not found');
        }

        return new EditNoteFormViewModel(note, this);
    };
}

export class NotesModel extends State<Record<string, never>> {
    constructor(private careMap: WithLinks<ICareMap>, private factory: CaremapNotesModelFactory) {
        super({});
        this.reactions = this.factory.reactionsFactory.create(this);
        this.factory.events.on('web-sockets.message', (msg => this.onWebsocketMessage(msg as IResourceChangedSocketMessage)));
    }

    private notes: ICaremapNoteResponse[] = [];
    reactions: ReactionsModel;

    hasLoaded = false;

    private onWebsocketMessage = (message: IResourceChangedSocketMessage) => {
        const impactedNote = this.notes.find(n => n.links.reactions.href.toLowerCase() === message.resourceToRefresh.toLowerCase());
        if (impactedNote){
            Promise.resolve().then(async () => {
                const reactions = await this.factory.notesClient.getNoteReactions(impactedNote);
                impactedNote.reactions = reactions;
                this.setState({ });
            }).catch(err => {
                this.factory.logger.error(`CaremapNotesModelFactory.onWebsocketMessage failed updating reactions. Inner error: ${err.toString()}`);
            });
        }
    };

    load = async () => {
        const notes = await this.factory.notesClient.getNotes(this.careMap);
        this.notes = notes;
        this.hasLoaded = true;
        this.setState({});
    };

    addNewNote = async (note: ICaremapNote) => {
        const response = await this.factory.notesClient.addNote({
            careMap: this.careMap,
            note
        });
        this.notes.push(response);
        this.setState({});
        return response;
    };

    putNote = async (note: WithLinks<ICaremapNote>) => {
        const response = await this.factory.notesClient.updateNote(note);
        this.notes = [
            ...this.notes.filter(n => n.noteId !== note.noteId),
            response
        ];
        this.setState({});
    };

    deleteNote = async (note: WithLinks<ICaremapNote>) => {
        await this.factory.notesClient.deleteNote(note);
        this.localDeleteNote(note);
    };

    private localDeleteNote = (note: ICaremapNote) => {
        this.notes = this.notes.filter(n => n.noteId !== note.noteId);

        // we mutated state, let subscribers know
        this.setState({});
    };

    getNotes = (): ICaremapNoteResponse[] => {
        return this.notes;
    };

    getNotesForTarget = (noteTarget: ICareMapNoteTarget): ICaremapNote[] => {
        return this.notes.filter(n => this.isSameTarget(n.otherAttributes.noteTarget, noteTarget));
    };

    private isSameTarget = (l: ICareMapNoteTarget, r: ICareMapNoteTarget) => {
        return l.section === r.section && l.memberId === r.memberId;
    };

    getNote = (noteId: string): ICaremapNoteResponse | undefined => {
        return this.getNotes().find(n => n.noteId === noteId);
    };

    getAttachmentCount = () => {
        return this.notes.reduce((count, note) => {
            return count + note.attachments.length;
        }, 0);
    };

    updateLocalReactions = ({ note, reactions }: { note: ICaremapNote; reactions: INoteReactions; }) => {
        note.reactions = reactions;
        this.setState({});
    };

}
